import type {
  Billing2Migration,
  BillingStorageGap,
  Cluster,
  CustomerDetail,
  CustomerSearchResult,
  Fleet,
  FleetOperation,
  FleetSelector,
  LatestDeployed,
  Namespace,
  Org,
  OrgToPurge,
  OrgSearchResult,
  OrgWithStats,
  ResolvedFleet,
  VersionGateBlock,
  VersionGateDetails,
} from "~/types";

function parseFleetOperation(op: FleetOperation): FleetOperation {
  return {
    ...op,
    created_at: new Date(op.created_at),
    updated_at: new Date(op.updated_at),
    children: op.children?.map((child) => ({
      ...child,
      started_at: new Date(child.started_at),
      ended_at: child.ended_at ? new Date(child.ended_at) : null,
    })),
    events: op.events?.map((event) => ({
      ...event,
      created_at: new Date(event.created_at),
    })),
  };
}

export class ApiError extends Error {
  status: number;
  statusText?: string;
  versionGateBlock?: VersionGateBlock;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

function versionGateBlock(body: any): VersionGateBlock | undefined {
  if (
    body?.code === "version_gate_blocked" &&
    body.gate &&
    Array.isArray(body.clusters)
  ) {
    return { gate: body.gate, clusters: body.clusters };
  }
}

interface ApiClient {
  baseUrl: string;

  getClusters(): Promise<Cluster[]>;
  getLatestDeployed(): Promise<LatestDeployed>;
  getCluster(id: string): Promise<Cluster>;
  getOrg(id: string): Promise<Org>;
  getCustomer(id: string): Promise<CustomerDetail>;
  searchCustomers(query?: string): Promise<CustomerSearchResult[]>;
  /** Requires a query; org search never lists all orgs. */
  searchOrgs(query: string): Promise<OrgSearchResult[]>;
  getOrgsToPurge(): Promise<OrgToPurge[]>;
  getNamespace(
    orgId: string,
    namespaceId: string,
    region: string,
  ): Promise<Namespace>;
  searchNamespacesByPrefix(idPrefix: string): Promise<Namespace[]>;
  listOrgNamespaces(orgId: string, regions: string[]): Promise<Namespace[]>;
  searchOrgNamespacesByPrefix(
    orgId: string,
    regions: string[],
    idPrefix: string,
  ): Promise<Namespace[]>;
  getClusterTopOrgs(clusterId: string): Promise<OrgWithStats[]>;
  createClusterOperation(clusterId: string, payload: object): Promise<string>;
  patchClusterTags(
    clusterId: string,
    tags: Record<string, string | null>,
  ): Promise<void>;
  createFleetOperation(payload: {
    operation_name: string;
    arguments: object;
    targets?: string[][];
    fleet?: string;
    description?: string;
    gates_enabled?: boolean;
    gate_bake_secs?: number;
  }): Promise<FleetOperation>;
  getFleet(name: string): Promise<Fleet | null>;
  resolveFleet(name: string, targetVersion?: string): Promise<ResolvedFleet>;
  getFleets(): Promise<Fleet[]>;
  putFleet(name: string, selectors: FleetSelector[]): Promise<Fleet>;
  getFleetOperations(): Promise<FleetOperation[]>;
  getVersionGates(): Promise<VersionGateDetails[]>;
  getFleetOperation(id: string): Promise<FleetOperation>;
  fleetOperationAction(
    id: string,
    action: "pause" | "resume" | "cancel",
  ): Promise<void>;
  refreshGitCache(): Promise<Cluster[]>;
  attachOrgToCustomer(
    customerId: string,
    orgId: string,
    orgName: string,
  ): Promise<any>;
  getRevision(): Promise<string | null>;
  getBilling2Migrations(): Promise<Billing2Migration[]>;
  setBilling2Cutover(clusterId: string): Promise<void>;
  setBilling2Rollback(clusterId: string): Promise<void>;
  mintBilling2StorageGap(
    clusterId: string,
    dryRun: boolean,
  ): Promise<BillingStorageGap>;
}

export const api: ApiClient = {
  baseUrl: "/api",

  async getClusters() {
    const response = await fetch(`${this.baseUrl}/clusters`);
    if (!response.ok) {
      const error = new Error(`Failed to fetch clusters`) as ApiError;
      error.status = response.status;
      error.statusText = response.statusText;
      throw error;
    }
    return response.json().then((data) =>
      data.clusters.map((cluster: Cluster) => ({
        ...cluster,
        created_at: new Date(cluster.created_at),
        updated_at: new Date(cluster.updated_at),
        operations: cluster.operations?.map((op) => ({
          ...op,
          created_at: new Date(op.created_at),
          updated_at: new Date(op.updated_at),
          status_transitions: op.status_transitions?.map((st) => ({
            ...st,
            created_at: new Date(st.created_at),
          })),
          operation_logs: op.operation_logs?.map((log) => ({
            ...log,
            timestamp: new Date(log.timestamp),
          })),
        })),
      })),
    );
  },

  async getLatestDeployed() {
    const response = await fetch(`${this.baseUrl}/clusters/latest-deployed`);
    if (!response.ok) {
      throw new ApiError(`Failed to fetch latest deployed`, response.status);
    }
    const data = await response.json();
    return {
      commit: data.commit ?? null,
      on_clusters: data.on_clusters ?? [],
      master: data.master ?? null,
    };
  },

  async getCluster(id: string) {
    const response = await fetch(`${this.baseUrl}/clusters/${id}`);
    if (!response.ok) {
      const error = new Error(`Failed to fetch cluster`) as ApiError;
      error.status = response.status;
      error.statusText = response.statusText;
      throw error;
    }
    return response.json().then((cluster: Cluster) => {
      console.log("Raw cluster data:", cluster);
      return {
        ...cluster,
        created_at: new Date(cluster.created_at),
        updated_at: new Date(cluster.updated_at),
        operations: (cluster.operations || []).map((op) => ({
          ...op,
          created_at: new Date(op.created_at),
          updated_at: new Date(op.updated_at),
          status_transitions: op.status_transitions?.map((st) => ({
            ...st,
            created_at: new Date(st.created_at),
          })),
          operation_logs: op.operation_logs?.map((log) => ({
            ...log,
            timestamp: new Date(log.timestamp),
          })),
        })),
      };
    });
  },

  async getOrg(id: string) {
    const response = await fetch(`${this.baseUrl}/orgs/${id}`);
    if (!response.ok) {
      const error = new Error(`Failed to fetch org`) as ApiError;
      error.status = response.status;
      error.statusText = response.statusText;
      throw error;
    }
    return response.json();
  },

  async getCustomer(id: string) {
    const response = await fetch(`${this.baseUrl}/customers/${id}`);
    if (!response.ok) {
      const error = new Error(`Failed to fetch customer`) as ApiError;
      error.status = response.status;
      error.statusText = response.statusText;
      throw error;
    }
    return response.json();
  },

  async searchCustomers(query?: string) {
    const params = new URLSearchParams();
    if (query) {
      params.set("q", query);
    }
    const url = `${this.baseUrl}/customers${params.toString() ? `?${params.toString()}` : ""}`;
    const response = await fetch(url);
    if (!response.ok) {
      const error = new Error(`Failed to search customers`) as ApiError;
      error.status = response.status;
      error.statusText = response.statusText;
      throw error;
    }
    const data = await response.json();
    return data.customers;
  },

  async searchOrgs(query: string) {
    const params = new URLSearchParams({ q: query });
    const url = `${this.baseUrl}/orgs?${params.toString()}`;
    const response = await fetch(url);
    if (!response.ok) {
      const error = new Error(`Failed to search orgs`) as ApiError;
      error.status = response.status;
      error.statusText = response.statusText;
      throw error;
    }
    const data = await response.json();
    return data.orgs;
  },

  async getOrgsToPurge() {
    const response = await fetch(`${this.baseUrl}/orgs-purge`);
    if (!response.ok) {
      const error = new Error(`Failed to fetch orgs to purge`) as ApiError;
      error.status = response.status;
      error.statusText = response.statusText;
      throw error;
    }
    const data = await response.json();
    return data.orgs;
  },

  async getClusterTopOrgs(clusterId: string) {
    const response = await fetch(
      `${this.baseUrl}/clusters/${clusterId}/top_orgs`,
    );
    if (!response.ok) {
      const error = new Error(`Failed to get cluster top orgs`) as ApiError;
      error.status = response.status;
      error.statusText = response.statusText;
      throw error;
    }
    const data = await response.json();
    return data.orgs;
  },

  async createFleetOperation(payload) {
    const response = await fetch(`${this.baseUrl}/fleet-operations`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      const error = new ApiError(
        body?.error ?? `Failed to create fleet operation`,
        response.status,
      );
      error.statusText = response.statusText;
      error.versionGateBlock = versionGateBlock(body);
      throw error;
    }
    return response.json().then(parseFleetOperation);
  },

  async getFleet(name: string) {
    const response = await fetch(`${this.baseUrl}/fleets/${name}`);
    if (response.status === 404) return null;
    if (!response.ok) {
      throw new ApiError("Failed to fetch fleet", response.status);
    }
    const fleet: Fleet = await response.json();
    return {
      ...fleet,
      created_at: new Date(fleet.created_at),
      updated_at: new Date(fleet.updated_at),
    };
  },

  async resolveFleet(name: string, targetVersion?: string) {
    const params = new URLSearchParams();
    if (targetVersion) params.set("target_version", targetVersion);
    const query = params.toString();
    const response = await fetch(
      `${this.baseUrl}/fleets/${name}/resolve${query ? `?${query}` : ""}`,
    );
    if (!response.ok) {
      throw new ApiError("Failed to resolve fleet", response.status);
    }
    return response.json();
  },

  async getFleets() {
    const response = await fetch(`${this.baseUrl}/fleets`);
    if (!response.ok) {
      throw new ApiError("Failed to fetch fleets", response.status);
    }
    const body = await response.json();
    return (body.fleets as Fleet[]).map((fleet) => ({
      ...fleet,
      created_at: new Date(fleet.created_at),
      updated_at: new Date(fleet.updated_at),
    }));
  },

  async putFleet(name: string, selectors: FleetSelector[]) {
    const response = await fetch(`${this.baseUrl}/fleets/${name}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ selectors }),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new ApiError(
        body?.error ?? "Failed to update fleet",
        response.status,
      );
    }
    return response.json().then((fleet: Fleet) => ({
      ...fleet,
      created_at: new Date(fleet.created_at),
      updated_at: new Date(fleet.updated_at),
    }));
  },

  async getFleetOperations() {
    const response = await fetch(`${this.baseUrl}/fleet-operations`);
    if (!response.ok) {
      const error = new Error(`Failed to fetch fleet operations`) as ApiError;
      error.status = response.status;
      error.statusText = response.statusText;
      throw error;
    }
    return response
      .json()
      .then((ops: FleetOperation[]) => ops.map(parseFleetOperation));
  },

  async getVersionGates() {
    const response = await fetch(`${this.baseUrl}/version-gates`);
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      throw new ApiError(
        body?.error ?? "Failed to fetch version gates",
        response.status,
      );
    }
    return response.json();
  },

  async getFleetOperation(id: string) {
    const response = await fetch(`${this.baseUrl}/fleet-operations/${id}`);
    if (!response.ok) {
      const error = new Error(`Failed to fetch fleet operation`) as ApiError;
      error.status = response.status;
      error.statusText = response.statusText;
      throw error;
    }
    return response.json().then(parseFleetOperation);
  },

  async fleetOperationAction(
    id: string,
    action: "pause" | "resume" | "cancel",
  ) {
    const response = await fetch(
      `${this.baseUrl}/fleet-operations/${id}/${action}`,
      { method: "POST" },
    );
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      const error = new Error(
        body?.error ?? `Failed to ${action} fleet operation`,
      ) as ApiError;
      error.status = response.status;
      error.statusText = response.statusText;
      throw error;
    }
  },

  async createClusterOperation(clusterId: string, payload: object) {
    const response = await fetch(`${this.baseUrl}/clusters/${clusterId}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      const body = await response.json().catch(() => null);
      const error = new ApiError(
        body?.error ?? `Request failed (HTTP ${response.status})`,
        response.status,
      );
      error.statusText = response.statusText;
      error.versionGateBlock = versionGateBlock(body);
      throw error;
    }

    const data = await response.json();
    return data.operation?.id;
  },

  // Merge-patch a cluster's custom tags: string values set keys, nulls remove
  // them, other keys are untouched (multi-select safe).
  async patchClusterTags(
    clusterId: string,
    tags: Record<string, string | null>,
  ) {
    const response = await fetch(`${this.baseUrl}/clusters/${clusterId}/tags`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ tags }),
    });
    if (!response.ok) {
      const body = await response.json().catch(() => null);
      const error = new Error(
        body?.error ?? `Failed to update tags (HTTP ${response.status})`,
      ) as ApiError;
      error.status = response.status;
      error.statusText = response.statusText;
      throw error;
    }
  },

  async refreshGitCache() {
    const response = await fetch(`${this.baseUrl}/clusters/refresh-git`, {
      method: "POST",
    });
    if (!response.ok) {
      const error = new Error(`Failed to refresh git cache`) as ApiError;
      error.status = response.status;
      error.statusText = response.statusText;
      throw error;
    }
    return response.json().then((data) =>
      data.clusters.map((cluster: Cluster) => ({
        ...cluster,
        created_at: new Date(cluster.created_at),
        updated_at: new Date(cluster.updated_at),
        operations: cluster.operations?.map((op) => ({
          ...op,
          created_at: new Date(op.created_at),
          updated_at: new Date(op.updated_at),
          status_transitions: op.status_transitions?.map((st) => ({
            ...st,
            created_at: new Date(st.created_at),
          })),
          operation_logs: op.operation_logs?.map((log) => ({
            ...log,
            timestamp: new Date(log.timestamp),
          })),
        })),
      })),
    );
  },

  async attachOrgToCustomer(
    customerId: string,
    orgId: string,
    orgName: string,
  ) {
    const response = await fetch(
      `${this.baseUrl}/customers/${customerId}/attach-org`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ org_id: orgId, org_name: orgName }),
      },
    );

    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      const error = new Error(
        body.message ?? "Failed to attach org to customer",
      ) as ApiError;
      error.status = response.status;
      error.statusText = response.statusText;
      throw error;
    }

    return await response.json();
  },

  async getRevision(): Promise<string | null> {
    return await fetch(`${this.baseUrl}/debug/revision`)
      .then((res) => res.json())
      .then((payload) => payload.revision)
      .catch(() => null);
  },

  async getBilling2Migrations() {
    const response = await fetch(`${this.baseUrl}/billing2-migrations`);
    if (!response.ok) {
      throw new ApiError(
        "Failed to fetch billing migration state",
        response.status,
      );
    }
    return (await response.json()).clusters;
  },

  async setBilling2Cutover(clusterId: string) {
    const response = await fetch(
      `${this.baseUrl}/billing2-migrations/${encodeURIComponent(clusterId)}/cutover`,
      { method: "POST" },
    );
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new ApiError(
        body.error ?? `Failed to update ${clusterId}`,
        response.status,
      );
    }
  },

  async setBilling2Rollback(clusterId: string) {
    const response = await fetch(
      `${this.baseUrl}/billing2-migrations/${encodeURIComponent(clusterId)}/rollback`,
      { method: "POST" },
    );
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new ApiError(
        body.error ?? `Failed to update ${clusterId}`,
        response.status,
      );
    }
  },

  async mintBilling2StorageGap(clusterId: string, dryRun: boolean) {
    const response = await fetch(
      `${this.baseUrl}/billing2-migrations/${encodeURIComponent(clusterId)}/mint-storage-gap?dry_run=${dryRun}`,
      { method: "POST" },
    );
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new ApiError(
        body.error ?? `Failed to mint storage gap for ${clusterId}`,
        response.status,
      );
    }
    return await response.json();
  },

  searchNamespacesByPrefix(idPrefix: string) {
    return fetchNamespaceList(this.baseUrl, { id_prefix: idPrefix });
  },

  listOrgNamespaces(orgId: string, regions: string[]) {
    return fetchNamespaceList(this.baseUrl, {
      org_id: orgId,
      regions: regions.join(","),
    });
  },

  searchOrgNamespacesByPrefix(
    orgId: string,
    regions: string[],
    idPrefix: string,
  ) {
    return fetchNamespaceList(this.baseUrl, {
      org_id: orgId,
      regions: regions.join(","),
      id_prefix: idPrefix,
    });
  },

  async getNamespace(orgId: string, namespaceId: string, region: string) {
    const namespaces = await fetchNamespaceList(this.baseUrl, {
      org_id: orgId,
      regions: region,
      id: namespaceId,
    });
    const namespace = namespaces[0];
    if (!namespace) {
      const error = new Error(`Namespace not found`) as ApiError;
      error.status = 404;
      error.statusText = "Not Found";
      throw error;
    }
    return namespace;
  },
};

async function fetchNamespaceList(
  baseUrl: string,
  params: Record<string, string>,
): Promise<Namespace[]> {
  const response = await fetch(
    `${baseUrl}/namespaces?${new URLSearchParams(params)}`,
  );
  if (!response.ok) {
    const error = new Error(`Failed to fetch namespaces`) as ApiError;
    error.status = response.status;
    error.statusText = response.statusText;
    throw error;
  }
  return (await response.json()).namespaces;
}
